@if (!AccessAllowed && CompletedLoading) {
  <div>
    <h3 style="border: 2px solid;border-radius: 6px;position: absolute;top: 50%;left: 50%;transform: translate(-50%, -50%);padding: 10px;">You don't have access to this function</h3>
  </div>
}

@if (AccessAllowed) {
  <div class="p-3 flex flex-column flex-auto">
    <h3 class="bf-title">Business Forecast <span class="bf-beta">Beta</span></h3>
    <p class="bf-intro">
      How many guests the next fortnight is likely to bring, worked out from this venue's own bookings: how far ahead
      guests usually book, how similar days have gone, the same week last year, holiday periods (any time set
      other than the default) and bank holidays, and the weather forecast. It gets better as more history builds
      up. Check it against the last 60 days below before relying on it.
    </p>

    @if (failedLoads.length > 0) {
      <div class="bf-warning">Could not load, so left out: {{failedLoads.join(', ')}}</div>
    }

    @if (loaded) {
      <div class="surface-card p-3 shadow-2 border-round bf-summary">
        @if (historyFrom) {
          Based on {{openDays}} days with bookings since {{historyFrom | date:'dd/MM/yy':'UTC'}}.
        } @else {
          There are no past bookings to learn from yet.
        }
        @if (!bands) {
          <span class="bf-dim"> Quiet / Normal / Busy bands appear once there are 14 days with bookings.</span>
        } @else {
          <span class="bf-dim"> Quiet under {{bands.normalFrom}} guests, Busy from {{bands.busyFrom}}, Very Busy from {{bands.veryBusyFrom}}.</span>
        }
        @if (weatherDays > 0) {
          <span class="bf-dim"> Weather for {{placeText}} from Open-Meteo.</span>
        }
        @if (missingCreated > 0) {
          <span class="bf-dim"> {{missingCreated}} bookings had no created date and are counted as made on the day.</span>
        }
      </div>

      <div class="bf-cards">
        @for (card of cards; track card.forecast.day) {
          <div class="surface-card shadow-2 border-round bf-card" [ngClass]="bandClass(card.band)" (click)="card.open = !card.open">
            <div class="bf-card-date">{{card.date | date:'EEE d MMM':'UTC'}}</div>
            <div class="bf-card-band">{{card.band ?? '—'}}</div>
            <div class="bf-card-estimate">
              @if (card.estimate != null) {
                ~{{card.estimate | number:'1.0-0'}} <span class="bf-dim">guests</span>
              } @else {
                <span class="bf-dim">No forecast</span>
              }
            </div>
            <div class="bf-dim bf-small">{{card.bookedNow}} booked so far</div>
            @if (card.forecast.holidayNames.length > 0) {
              <div class="bf-holiday"><i class="pi pi-calendar"></i> {{card.forecast.holidayNames.join(', ')}}</div>
            }
            @if (card.forecast.weather) {
              <div class="bf-weather">
                <i class="pi" [ngClass]="card.forecast.weather.rain >= 3 ? 'pi-cloud' : 'pi-sun'"></i>
                {{card.forecast.weather.tmax | number:'1.0-0'}}°C · {{card.forecast.weather.rain | number:'1.0-1'}} mm
              </div>
            }
            @if (card.open) {
              <ul class="bf-reasons">
                @for (reason of card.forecast.reasons; track $index) {
                  <li>{{reason}}</li>
                }
              </ul>
            } @else {
              <div class="bf-dim bf-small bf-why">How was this worked out?</div>
            }
          </div>
        }
      </div>

      <div class="surface-card p-3 shadow-2 border-round bf-backtest">
        <div class="bf-backtest-head">
          <h4>
            How it would have done over the last 60 days
            <button type="button" class="bf-info-btn" (click)="showHelp = !showHelp" [attr.aria-expanded]="showHelp" title="What do these figures mean?">
              <i class="pi pi-info-circle"></i>
            </button>
          </h4>
          <p-selectbutton [options]="leadOptions" [(ngModel)]="backtestLead" optionLabel="label" optionValue="value" [allowEmpty]="false" (onChange)="runBacktest()"></p-selectbutton>
        </div>

        @if (showHelp) {
          <div class="bf-help">
            <button type="button" class="bf-help-close" (click)="showHelp = false" title="Close"><i class="pi pi-times"></i></button>
            <h5>What do these figures mean?</h5>
            <p>
              This is a test of how good the forecast is, so you can decide how much to trust it.
              We pretended the forecast already existed. For each of the last 60 days, we used only what we knew
              <b>{{backtestLead}} {{backtestLead == 1 ? 'day' : 'days'}} before</b> that day to make a forecast,
              then compared it with the number of guests who actually came.
            </p>
            @if (backtestRows.length == 0) {
              <p>There isn't enough booking history yet to run this test. Check back once the venue has a few more weeks of bookings.</p>
            } @else {
              <dl>
                <dt>{{backtestRows.length}} days tested</dt>
                <dd>
                  The number of days we could check. Days with no bookings (for example when you were closed) are left out,
                  and so are days when there wasn't enough history yet to make a forecast.
                </dd>

                <dt>±{{backtestAverageMiss | number:'1.0-0'}} guests out on average</dt>
                <dd>
                  On average, the forecast was {{backtestAverageMiss | number:'1.0-0'}} {{(backtestAverageMiss | number:'1.0-0') == '1' ? 'guest' : 'guests'}}
                  away from what really happened - sometimes too high, sometimes too low.
                  Your days had {{backtestAverageActual | number:'1.0-0'}} guests on average, so compare the two:
                  being a few guests out on a day of {{backtestAverageActual | number:'1.0-0'}} is
                  {{backtestAverageMiss <= backtestAverageActual * 0.2 ? 'pretty close' : backtestAverageMiss <= backtestAverageActual * 0.4 ? 'a fair guide, but not exact' : 'still quite rough'}}.
                </dd>

                <dt>{{backtestWithin20 * 100 | number:'1.0-0'}}% of days within 20%</dt>
                <dd>
                  How often the forecast was close to the real number. "Within 20%" means near enough: on a day of
                  50 guests, anything from 40 to 60 counts.
                  @if (backtestAverageActual < 15) {
                    <br><span class="bf-help-note">Your days are quite small, so this figure looks harsher than it really is. On a day of
                    {{backtestAverageActual | number:'1.0-0'}} guests, being out by just 1 or 2 already counts as more than 20%.
                    The figure above (guests out on average) is the fairer one to look at.</span>
                  }
                </dd>

                @if (backtestBandRows > 0) {
                  <dt>{{backtestBandRight * 100 | number:'1.0-0'}}% of days in the right band</dt>
                  <dd>
                    How often the forecast picked the right kind of day - Quiet, Normal, Busy or Very Busy - when it was
                    compared with what actually happened. If you mainly want to know "do I need extra staff?",
                    this is the figure that matters most.
                  </dd>
                }

                <dt>The chart</dt>
                <dd>
                  The <span class="bf-help-actual">blue line</span> is how many guests actually came each day. The
                  <span class="bf-help-forecast">orange dashed line</span> is what the forecast said. The closer the two lines are, the better.
                </dd>
              </dl>
            }
            <p class="bf-dim">
              Use the buttons to see how it does further ahead - it is usually more accurate closer to the day.
              The forecast learns from your bookings, so it gets better as more history builds up.
            </p>
          </div>
        }

        @if (backtestRows.length == 0) {
          <p class="bf-dim">Not enough history yet to test the forecast this far ahead.</p>
        } @else {
          <div class="bf-metrics">
            <div><b>{{backtestRows.length}}</b><span>days tested</span></div>
            <div><b>±{{backtestAverageMiss | number:'1.0-0'}}</b><span>guests out on average (days average {{backtestAverageActual | number:'1.0-0'}})</span></div>
            <div><b>{{backtestWithin20 * 100 | number:'1.0-0'}}%</b><span>of days within 20%</span></div>
            @if (backtestBandRows > 0) {
              <div><b>{{backtestBandRight * 100 | number:'1.0-0'}}%</b><span>of days in the right band</span></div>
            }
          </div>
          <div class="bf-chart">
            <p-chart type="line" [data]="chartData" [options]="chartOptions" height="300px"></p-chart>
          </div>
        }
      </div>
    }
  </div>
}
